'use client';

import { useCallback, useMemo } from 'react';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type CopilotPlanMessage,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import { useTranslations } from 'next-intl';
import { useChatInstancesStore } from '../store/chatInstancesStore';
import { useMainContentTabs } from '../store/layoutStore';
import { sendCopilotGraphMessage } from './prepare-chat-user-turn';

function extractPlanMessages(
  messages: TerraUIMessage[],
): Array<{ partId: string; data: CopilotPlanMessage }> {
  const results: Array<{ partId: string; data: CopilotPlanMessage }> = [];

  for (const message of messages) {
    if (message.role !== 'assistant' || !message.parts) {
      continue;
    }

    for (const part of message.parts) {
      if (
        part.type === TerraUIMessageDataPartName &&
        part.data?.type === DataMessageTypeEnum.CopilotPlan
      ) {
        const data = part.data as CopilotPlanMessage;

        results.push({ partId: `${message.id}:${data.id}`, data });
      }
    }
  }

  return results;
}

export function useCopilotPlan({
  chatId,
  messages,
}: {
  chatId: string;
  messages: TerraUIMessage[];
}): { latestPlan: CopilotPlanMessage | null; openPlanTab: () => void } {
  const t = useTranslations('chat.copilot.plan');
  const { openOrFocusTab } = useMainContentTabs();

  const latestPlan = useMemo(() => {
    const plans = extractPlanMessages(messages);

    return plans.length > 0 ? plans[plans.length - 1].data : null;
  }, [messages]);

  const openPlanForData = useCallback(
    (plan: CopilotPlanMessage) => {
      openOrFocusTab({
        id: `plan-viewer-${chatId}`,
        title: plan.title || t('tabTitle'),
        type: 'plan-viewer',
        data: { chatId, filePath: plan.filePath },
      });
    },
    [chatId, openOrFocusTab, t],
  );

  const openPlanTab = useCallback(() => {
    if (latestPlan) {
      openPlanForData(latestPlan);
    }
  }, [latestPlan, openPlanForData]);

  return { latestPlan, openPlanTab };
}

export function useExecuteCopilotPlan(chatId: string): () => void {
  const t = useTranslations('chat.copilot.plan');
  const setChatMode = useChatInstancesStore((state) => state.setChatMode);
  const sendMessageToChat = useChatInstancesStore(
    (state) => state.sendMessageToChat,
  );

  return useCallback(() => {
    setChatMode(chatId, 'agent');
    void sendCopilotGraphMessage({
      chatId,
      send: ({ markStarted }) => {
        markStarted();

        return sendMessageToChat(chatId, {
          id: crypto.randomUUID(),
          role: 'user',
          parts: [{ type: 'text', text: t('buildMessage') }],
        });
      },
    }).catch((sendError: unknown) => {
      console.error('Attack plan sendMessage failed', sendError);
    });
  }, [chatId, sendMessageToChat, setChatMode, t]);
}
